<template>
    <chart-template :title="$t('Performance')">
        <div class="row charts">
            <v-chart
                v-if="comparisonReport"
                class="col"
                :option="comparisonReportOptions"
                autoresize
            />

            <v-chart class="col" :option="reportOptions" autoresize />
        </div>
    </chart-template>
</template>

<script>
import { formatDate } from '@utils/dateRangeFormatter.js';
import { colors } from 'quasar';
import ChartTemplate from './ChartTemplate.vue';
const { getPaletteColor } = colors;
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

import { BarChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent } from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';

use([CanvasRenderer, BarChart, TitleComponent, TooltipComponent]);

export default {
    name: 'KPIPerformanceChart',
    components: { VChart, ChartTemplate },
    props: {
        report: {
            type: Object,
            required: true
        },
        comparisonReport: {
            type: Object,
            required: false
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        reportOptions() {
            const legends = this.getTitle(
                this.report,
                this.$t('Current Period')
            );
            return this.getChartOptions(this.report, legends);
        },
        comparisonReportOptions() {
            const legends = this.getTitle(
                this.comparisonReport,
                this.$t('Comparison Period')
            );
            return this.getChartOptions(this.comparisonReport, legends);
        },
        chartGrid() {
            if (this.comparisonReport) {
                return {
                    top: 20,
                    bottom: 70
                };
            }
            return null;
        }
    },
    methods: {
        /**
         * Get chart title if comparison report is available.
         * @param {Object} report
         * @param {String} chartTitle
         * @returns {Object} object containing chart title and subtitle
         */
        getTitle(report, chartTitle) {
            let title = null;
            let subtitle = null;
            if (this.comparisonReport) {
                title = chartTitle;
                subtitle = formatDate(
                    report.dateInterval.start,
                    report.dateInterval.end,
                    this.userLocale
                );
            }
            return { title: title, subtitle: subtitle };
        },
        /**
         * Get the chart definition for the echart component.
         * @param {Object} report object
         * @param {Object} legends object containing chart title and subtitle
         * @returns {Object} full chart options object
         */
        getChartOptions(report, legends) {
            return {
                title: {
                    text: legends.title,
                    textStyle: {
                        fontSize: 14
                    },
                    subtext: legends.subtitle,
                    subtextStyle: {
                        fontSize: 14
                    },
                    bottom: 0,
                    left: 'center'
                },
                grid: {
                    ...this.chartGrid,
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: params => {
                        const [item] = params;
                        return `${item.name}: <b>${item.value.toFixed(2)} ${this.$t('count/min')}</b>`;
                    }
                },
                xAxis: {
                    type: 'category',
                    data: [this.$t('Actual rate'), this.$t('Max run rate')],
                    axisLabel: {
                        fontSize: 13,
                        color: 'black'
                    }
                },
                yAxis: {
                    type: 'value',
                    name: this.$t('count/min'),
                    nameLocation: 'middle',
                    nameGap: 30,
                    nameTextStyle: {
                        fontSize: 13,
                        color: 'black'
                    }
                },
                series: [
                    {
                        type: 'bar',
                        data: [
                            {
                                value: report.totals.performance.actualRate,
                                itemStyle: {
                                    color: getPaletteColor('light-green')
                                }
                            },
                            {
                                value: report.totals.performance.plannedRate,
                                itemStyle: {
                                    color: getPaletteColor('lighter-green')
                                }
                            }
                        ]
                    }
                ]
            };
        }
    }
};
</script>

<style lang="scss" scoped>
@import './dashboard.scss';
.charts {
    padding-bottom: 10px;
    height: 410px !important;
    max-width: 100%;
}
</style>
